import React, { useState, useEffect, useCallback } from 'react';
import './ReviewsCarousel.css';

const reviews = [
    {
        id: 1,
        text: "Veloce heeft ons proces volledig geautomatiseerd. We besparen nu meer dan 20 uur per week aan handmatig werk. Een absolute game-changer voor ons team.",
        author: "Jan de Vries",
        role: "CEO",
        initials: "JD"
    },
    {
        id: 2,
        text: "De AI-implementatie was naadloos en professioneel. Onze omzet steeg binnen 2 maanden met 15% dankzij de slimme klantinzichten die we nu hebben.",
        author: "Sarah Jansen",
        role: "Marketing Director",
        initials: "SJ"
    },
    {
        id: 3,
        text: "Professioneel, snel en denken echt mee. Eindelijk een partij die niet alleen code tikt, maar begrijpt wat onze business nodig heeft om te groeien.",
        author: "Mark Peters",
        role: "Founder",
        initials: "MP"
    },
    {
        id: 4,
        text: "Onze oude software was traag en foutgevoelig. De nieuwe maatwerkoplossing werkt perfect en heeft onze efficiëntie direct verbeterd.",
        author: "Lisa Bakker",
        role: "Operations Manager",
        initials: "LB"
    },
    {
        id: 5,
        text: "Vanaf het eerste gesprek voelden we ons begrepen. De oplevering was precies op tijd en binnen budget. Aanrader voor elk MKB bedrijf.",
        author: "Tom Verhoeven",
        role: "Directeur",
        initials: "TV"
    }
];

export default function ReviewsCarousel() {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [viewCount, setViewCount] = useState(3);

    // Responsive visible items
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) setViewCount(1);
            else if (window.innerWidth < 1024) setViewCount(2);
            else setViewCount(3);
        };

        handleResize(); // Init
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto scroll
    useEffect(() => {
        if (isPaused) return;

        const interval = setInterval(() => {
            setActiveIndex((current) => {
                // Return to 0 seamlessly if possible, or just loop
                return current === reviews.length - viewCount ? 0 : current + 1;
            });
        }, 5000);

        return () => clearInterval(interval);
    }, [isPaused, viewCount]);

    const maxIndex = reviews.length - viewCount;
    // ensure activeIndex doesn't exceed valid bounds when resizing
    useEffect(() => {
        if (activeIndex > maxIndex && maxIndex >= 0) {
            setActiveIndex(maxIndex);
        }
    }, [viewCount, maxIndex, activeIndex]);

    const gap = viewCount === 1 ? 1 : 2; // 1rem mobile, 2rem desktop

    const handlePrev = useCallback(() => {
        setActiveIndex((current) => current === 0 ? reviews.length - viewCount : current - 1);
    }, [viewCount]);

    const handleNext = useCallback(() => {
        setActiveIndex((current) => current === reviews.length - viewCount ? 0 : current + 1);
    }, [viewCount]);

    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    // Minimum swipe distance (in px) 
    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null); // Reset
        setTouchStart(e.targetTouches[0].clientX);
        setIsPaused(true);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            handleNext();
        }
        if (isRightSwipe) {
            handlePrev();
        }

        setIsPaused(false);
    };

    return (
        <section className="reviews-section" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
            <div className="reviews-container">
                <h2>Wat zeggen <span>onze klanten?</span></h2>

                <div className="carousel-wrapper">
                    <div className="carousel-button-group">
                        <button className="carousel-arrow left" onClick={handlePrev} aria-label="Previous slide">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button className="carousel-arrow right" onClick={handleNext} aria-label="Next slide">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    <div className="carousel-viewport">
                        <div
                            className="carousel-track"
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onTouchEnd}
                            style={{
                                transform: `translateX(calc(-${activeIndex} * (100% + ${gap}rem) / ${viewCount}))`
                            }}
                        >
                            {reviews.map((review) => (
                                <div key={review.id} className="review-card">
                                    <div className="stars">★★★★★</div>
                                    <p className="review-text">"{review.text}"</p>
                                    <div className="reviewer-info">
                                        <div className="reviewer-avatar">{review.initials}</div>
                                        <div className="reviewer-details">
                                            <h4>{review.author}</h4>
                                            <p>{review.role}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="carousel-dots">
                    {Array.from({ length: reviews.length - viewCount + 1 }).map((_, idx) => (
                        <button
                            key={idx}
                            className={`dot ${idx === activeIndex ? 'active' : ''}`}
                            onClick={() => setActiveIndex(idx)}
                            aria-label={`Go to slide ${idx + 1}`}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
